import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../components/CatalogListHeader/CatalogListHeader';
import EmptyListResult from '../../components/EmptyListResult/EmptyListResult';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import { showNotification } from '../../utils/Notification/Notification';
import { ActionIcon, Button, Grid, Skeleton, TextInput, Tooltip } from '@mantine/core';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { useLocation } from 'react-router-dom';
import {
  IconPrinter,
  IconReceiptDollar,
  IconTrash,
  IconTruckDelivery
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import CatalogContainer from '../../components/CatalogContainer/CatalogContainer';
import CatalogCard from '../../components/CatalogCard/CatalogCard';
import OutsourcedOrderPrintTicketDrawer from '../../components/OutsourcedOrderPrintTicketDrawer';
import { useGetOutsourcedOrders } from '../../hooks/outsourcingOrders/queries';
import {
  useUpdateOutsourcedOrderById,
  useDeleteOutsourcedOrderById
} from '../../hooks/outsourcingOrders/mutations';
import { IOutsourcedOrder } from '../../hooks/outsourcingOrders/interfaces';
import { IOrder } from '../../interfaces/Order';
import { useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { IOrderProduct } from '../../interfaces/Product';
import debounce from 'lodash/debounce';
import { DEFAULT_FOLIO_PREFIX } from '../../constants';
import { useGetStoreById } from '../../hooks/stores/queries';
import { useSelector } from 'react-redux';

function resolvePartnerName(partner: IOutsourcedOrder['partner']): string {
  if (!partner || typeof partner === 'string') return '';
  return partner.name?.trim() || '';
}

const OutsourcingOrdersList = () => {
  const { t } = useTranslation(['FieldName', 'CommonButtons', 'Notifications', 'OutsourcingPage']);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const { company } = useSelector((state: any) => state.user);

  //FILTER SEARCH
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [
    openedPrintTicketDrawer,
    { open: openPrintTicketDrawer, close: closePrintTicketDrawer }
  ] = useDisclosure(false);
  const [printTicketOutsourcedOrder, setPrintTicketOutsourcedOrder] =
    useState<IOutsourcedOrder | null>(null);
  const searchTermInputRef = useRef<HTMLInputElement>(null);
  const setSearchParamsDebounced = debounce((val: string) => {
    setSearchTerm(val || undefined);
  }, 500);

  const {
    isLoading: isGetOutsourcedOrdersLoading,
    data,
    fetchNextPage,
    hasNextPage,
    refetch: refetchList
  } = useGetOutsourcedOrders(!!searchParamStoreId, {
    storeId: searchParamStoreId,
    sequence: searchTerm,
    statuses: ['pending', 'in_process'],
    limit: 10
  });
  const {
    isLoading: isGetStoreByIdLoading,
    data: selectedStore
  } = useGetStoreById(!!searchParamStoreId, searchParamStoreId);

  const clearSearchTerm = () => {
    if (searchTermInputRef.current) {
      searchTermInputRef.current.value = ''; // Reset the input field value via ref
      setSearchParamsDebounced(undefined); // Reset the state to trigger re-render
    }
  };

  const updateOutsourcedOrderById = useUpdateOutsourcedOrderById();
  const deleteOutsourcedOrderById = useDeleteOutsourcedOrderById();

  const outsourcedOrders = useMemo(() => {
    return data?.pages.reduce((list, { data }: { data: IOutsourcedOrder[] }) => {
      list.push(...data);
      return list;
    }, [] as IOutsourcedOrder[]);
  }, [data]);

  const handleOpenPrintTicketDrawer = (outsourcedOrder: IOutsourcedOrder) => {
    setPrintTicketOutsourcedOrder(outsourcedOrder);
    openPrintTicketDrawer();
  };

  const handleClosePrintTicketDrawer = () => {
    closePrintTicketDrawer();
    setPrintTicketOutsourcedOrder(null);
  };

  return (
    <CatalogContainer pos="relative">
      <CatalogListHeader title={t('OutsourcingPage:title')} showCreateButton={false} />
      <OutsourcedOrderPrintTicketDrawer
        opened={openedPrintTicketDrawer}
        onClose={handleClosePrintTicketDrawer}
        outsourcedOrder={printTicketOutsourcedOrder}
        store={selectedStore || null}
        companyLogoUrl={company?.logoUrl}
      />
      <LoaderOverlay
        visible={
          isGetOutsourcedOrdersLoading ||
          isGetStoreByIdLoading ||
          updateOutsourcedOrderById.isPending ||
          deleteOutsourcedOrderById.isPending
        }
      />
      <Grid>
        <Grid.Col span={12} my={10}>
          <Grid align="center" justify="center">
            <Grid.Col span={{ xs: 12, md: 10 }}>
              <TextInput
                ref={searchTermInputRef}
                onChange={(event) => setSearchParamsDebounced(event.currentTarget.value)}
                leftSection={`${DEFAULT_FOLIO_PREFIX}-`}
                label={`${t('FieldName:order')}`}
              />
            </Grid.Col>
            <Grid.Col span={{ xs: 12, md: 2 }} style={{ alignSelf: 'flex-end' }}>
              <Button fullWidth type="button" color="gray" onClick={clearSearchTerm}>
                {t('CommonButtons:clear').toUpperCase()}
              </Button>
            </Grid.Col>
          </Grid>
        </Grid.Col>
      </Grid>
      {outsourcedOrders && outsourcedOrders.length === 0 && <EmptyListResult />}
      <InfiniteScroll
        dataLength={outsourcedOrders?.length || 0}
        next={fetchNextPage}
        hasMore={hasNextPage}
        style={{ overflow: 'hidden', position: 'relative' }}
        loader={
          <>
            <Skeleton height={10} my={10} radius="md" />
            <Skeleton height={10} my={10} radius="md" />
            <Skeleton height={10} my={10} radius="md" />
          </>
        }
        // endMessage={<Empty description={false} />}
      >
        <Grid gutter="md">
          {outsourcedOrders?.map((outsourcedOrder: IOutsourcedOrder) => {
            return (
              <Grid.Col key={outsourcedOrder._id} span={{ xs: 12, sm: 6, lg: 4 }}>
                <CatalogCard
                  fields={[
                    {
                      label: t('FieldName:order'),
                      value: `${DEFAULT_FOLIO_PREFIX}-${(outsourcedOrder?.order as IOrder)?.sequence}`
                    },
                    {
                      label: t('FieldName:outsourcingPartner'),
                      value: resolvePartnerName(outsourcedOrder?.partner)
                    },
                    {
                      label: t('FieldName:status'),
                      value:
                        t(`FieldName:outsourcedStatuses.${outsourcedOrder?.outsourcedStatus}`) || ''
                    },
                    {
                      label: t('FieldName:product'),
                      value:
                        (outsourcedOrder?.relatedProduct as IOrderProduct)?.name ||
                        t('OutsourcingPage:productNotInOrder')
                    },
                    {
                      label: t('FieldName:quantity'),
                      value: (outsourcedOrder?.relatedProduct as IOrderProduct)?.quantity || 0
                    }
                  ]}
                  cardActionButtons={[
                    {
                      action: () =>
                        ConfirmationModal({
                          title: t('OutsourcingPage:setAsInProcessConfirmation'),
                          onConfirm: async () => {
                            await updateOutsourcedOrderById.mutateAsync({
                              id: outsourcedOrder._id,
                              outsourcedStatus: 'in_process',
                              sentDate: new Date().toISOString()
                            });
                            showNotification({
                              type: 'Default',
                              title: t('Notifications:success')
                            });
                            await refetchList();
                          },
                          onCancel: () => {}
                        }),
                      disabled: outsourcedOrder.outsourcedStatus !== 'pending',
                      Icon: <IconTruckDelivery />,
                      tooltip: t('OutsourcingPage:setAsInProcess')
                    },
                    {
                      action: () =>
                        ConfirmationModal({
                          title: t('OutsourcingPage:setAsFinishedConfirmation'),
                          onConfirm: async () => {
                            await updateOutsourcedOrderById.mutateAsync({
                              id: outsourcedOrder._id,
                              outsourcedStatus: 'returned',
                              receivedDate: new Date().toISOString()
                            });
                            showNotification({
                              type: 'Default',
                              title: t('Notifications:success')
                            });
                            await refetchList();
                          },
                          onCancel: () => {}
                        }),
                      disabled: outsourcedOrder.outsourcedStatus !== 'in_process',
                      Icon: <IconTruckDelivery style={{ transform: 'scaleX(-1)' }} />,
                      tooltip: t('OutsourcingPage:setAsFinished')
                    },
                    {
                      action: () =>
                        ConfirmationModal({
                          title: t('Notifications:CRUD.deleteConfirmation'),
                          onConfirm: async () => {
                            await deleteOutsourcedOrderById.mutateAsync(outsourcedOrder._id);
                            showNotification({
                              type: 'Default',
                              title: t('Notifications:success')
                            });
                            await refetchList();
                          },
                          onCancel: () => {}
                        }),
                      disabled: false,
                      Icon: <IconTrash />,
                      tooltip: t('CommonButtons:delete')
                    }
                  ]}
                  cardTitle={`PM-${outsourcedOrder?.outsourcedOrderSequence}` || ''}
                  cardTitleExtra={
                    <Tooltip
                      withinPortal={false}
                      label={t('OutsourcingPage:printOutsourcedOrderTicket')}
                    >
                      <ActionIcon
                        aria-label={t('OutsourcingPage:printOutsourcedOrderTicket')}
                        onClick={() => handleOpenPrintTicketDrawer(outsourcedOrder)}
                        variant="transparent"
                      >
                        <IconPrinter />
                      </ActionIcon>
                    </Tooltip>
                  }
                  TitleIcon={IconReceiptDollar}
                />
              </Grid.Col>
            );
          })}
        </Grid>
      </InfiniteScroll>
    </CatalogContainer>
  );
};

export default OutsourcingOrdersList;
